import { computed, toValue, type Ref } from 'vue'

import { useAuth } from '@common/auth-layer/composables/useAuth'
import { useCommunityUser } from '@communities/community-user-layer/composables/useCommunityUser'
import { useGroups } from '@communities/groups-layer/composables/useGroups'
import { GroupType, MemberStatus } from '@communities/groups-layer/types/types'
import type {
  AccessControlOptions,
  AccessControlResult,
  AccessControlUserGroupDetails,
} from '@communities/access-control-layer/types/access-control'

/**
 * Centralized access control for communities resources (channels, posts, members, …).
 * Lives under `access-control-layer` so it can depend on groups + community-user without
 * pulling communities domain into `@common/*`.
 */
export const useAccessControl = (options: AccessControlOptions): AccessControlResult => {
  const { groupId, requireActiveMembership = true, customRules } = options

  const { store: authStore } = useAuth()
  const groups = useGroups()
  const communityUser = useCommunityUser()

  const currentGroup = computed(() => groups.store.currentGroup)
  const locationId = computed(() => currentGroup.value?.locationId)

  const userGroupDetails = communityUser.fetchCurrentUserDetailsForGroup(
    groupId,
    locationId,
    computed(() => authStore.isLoggedIn)
  )

  const canAccess = computed(() => {
    const gId = toValue(groupId)
    const group = currentGroup.value
    const userDetails = userGroupDetails.data.value

    if (!gId) return false
    if (!group) return false

    if (group.settings?.groupType !== GroupType.PRIVATE) {
      return true
    }

    if (!authStore.isLoggedIn) return false

    if (group.settings?.groupType === GroupType.PRIVATE) {
      const hasActiveMembership = userDetails?.status === MemberStatus.ACTIVE

      if (requireActiveMembership && !hasActiveMembership) {
        return false
      }

      if (customRules && userDetails) {
        return customRules(group, userDetails as unknown as AccessControlUserGroupDetails)
      }

      return hasActiveMembership
    }

    return false
  })

  const isChecking = computed(() => {
    const isUserGroupQueryEnabled = userGroupDetails.isEnabled?.value ?? true
    const isUserGroupQueryChecking = isUserGroupQueryEnabled && userGroupDetails.isLoading.value

    return authStore.isChecking || isUserGroupQueryChecking || !currentGroup.value
  })

  const accessReason = computed(() => {
    const gId = toValue(groupId)
    const group = currentGroup.value
    const userDetails = userGroupDetails.data.value

    if (isChecking.value) return 'checking'
    if (!gId) return 'no_group'
    if (!group) return 'no_group'
    if (!authStore.isLoggedIn) return 'not_authenticated'
    if (group.settings?.groupType === GroupType.PRIVATE && userDetails?.status !== MemberStatus.ACTIVE) {
      return 'not_member'
    }
    if (canAccess.value) return 'granted'
    return 'denied'
  })

  const canCallApi = computed(() => {
    const gId = toValue(groupId)
    const lId = currentGroup.value?.locationId
    return !!gId && !!lId && !isChecking.value && canAccess.value
  })

  const assertCanCallApi = (action: string) => {
    const gId = toValue(groupId)
    if (!gId) {
      throw new Error(`Group ID is required for ${action}`)
    }

    const lId = currentGroup.value?.locationId
    if (!lId) {
      throw new Error(`Location ID is required for ${action}`)
    }

    if (isChecking.value) {
      throw new Error(`Access check in progress for ${action}`)
    }

    if (!canAccess.value) {
      throw new Error(`Access denied for ${action}`)
    }

    return { groupId: gId, locationId: lId }
  }

  const assertAuthenticatedApiContext = (action: string) => {
    const gId = toValue(groupId)
    if (!gId) {
      throw new Error(`Group ID is required for ${action}`)
    }

    const lId = currentGroup.value?.locationId
    if (!lId) {
      throw new Error(`Location ID is required for ${action}`)
    }

    if (isChecking.value) {
      throw new Error(`Access check in progress for ${action}`)
    }

    if (!authStore.isLoggedIn) {
      throw new Error(`Authentication required for ${action}`)
    }

    return { groupId: gId, locationId: lId }
  }

  return {
    canAccess,
    isChecking,
    canCallApi,
    currentGroup,
    userDetails: userGroupDetails.data as Ref<AccessControlUserGroupDetails | undefined>,
    assertCanCallApi,
    assertAuthenticatedApiContext,
    accessReason,
  }
}
